import {
  LineChart,
  Receipt,
  Target,
  CalendarClock,
  CircleDollarSign,
  UsersRound,
  UserCog,
  Upload,
} from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import ProTip from '../ProTip'

export default function SalesTrackersSection() {
  return (
    <>
      <p className="text-sm text-muted-foreground mb-3">
        Sales Trackers is the record of what was actually invoiced, taken from QuickBooks rather
        than from the CRM pipeline. It answers what the company and each rep have billed, how that
        compares with their targets, and which invoice lines make up the total. It is a separate
        question from the opportunity pipeline, which is about what might close.
      </p>

      <section id="st-overview" aria-labelledby="st-overview-heading">
        <div className="flex items-center gap-2 mb-2">
          <LineChart className="h-4 w-4" aria-hidden="true" />
          <h3 id="st-overview-heading" className="font-semibold">
            Overview and choosing a period
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Overview</span> is the company picture for whichever period
          you have selected: total sales by rep, product category by month, the software, service
          and remediation split, and top clients. Where a target has been set, it is shown against
          the actual figure with the difference and the percentage above or below.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          Every Sales Trackers page shares one period selector, and it carries across as you move
          between them:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Month</span>: a single month. This is what you land on,
            because the most common question is how the current month is going.
          </li>
          <li>
            <span className="font-medium">Quarter</span>: a three month slice of the fiscal year, so
            Q1 is March to May and Q4 is December to February.
          </li>
          <li>
            <span className="font-medium">Year</span>: the whole fiscal year.
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          The fiscal year runs <span className="font-medium">March to the end of February</span> and
          is named after the year it <span className="font-medium">finishes</span> in, so FY2026
          means March 2025 to February 2026. The Australian entity reports on a calendar year, but
          its sales are placed in whichever fiscal year contains their date rather than being
          shifted, so they line up with everything else.
        </p>
        <ProTip>
          <span className="font-medium">Every figure on Sales Trackers is Canadian dollars.</span>{' '}
          Sales in other currencies are converted at the rate frozen on the upload that brought them
          in, which is the basis the original Sales Tracker workbook used. There is no currency
          selector to get wrong.
        </ProTip>
      </section>
      <Separator />

      <section id="st-my-sales" aria-labelledby="st-my-sales-heading">
        <div className="flex items-center gap-2 mb-2">
          <UsersRound className="h-4 w-4" aria-hidden="true" />
          <h3 id="st-my-sales-heading" className="font-semibold">
            Your own sales
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          If you are a sales rep, Sales Trackers shows you two things and nothing else:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">My sales</span>: your own sheet. Your total, your sales by
            product, your software and service split, your targets, and how you are tracking against
            them for the period you have chosen.
          </li>
          <li>
            <span className="font-medium">My transactions</span>: the individual invoice lines
            behind your total, so you can see exactly which sales produced the figure.
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          Both pages always show <span className="font-medium">you</span>. They work out who you are
          from your login, so there is nothing to select and no way to land on somebody else&rsquo;s
          numbers. Company-wide pages and other reps&rsquo; sheets are restricted to administrators.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          If a sale you expected is missing, it is usually attribution rather than a lost sale: the
          sales account on the invoice has not yet been linked to you. An administrator can fix that
          on <span className="font-medium">Products &amp; reps</span>.
        </p>
      </section>
      <Separator />

      <section id="st-breakdowns" aria-labelledby="st-breakdowns-heading">
        <div className="flex items-center gap-2 mb-2">
          <CircleDollarSign className="h-4 w-4" aria-hidden="true" />
          <h3 id="st-breakdowns-heading" className="font-semibold">
            By Product and By Rep (Administrators)
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">By Product</span> breaks the period down by what was sold.{' '}
          <span className="font-medium">By Rep</span> asks you to choose a rep first and then shows
          that one person&rsquo;s sheet: their total, their sales by product, their software and
          service split, their targets and how they are tracking. Their individual billing lines sit
          underneath.
        </p>
        <p className="text-sm text-muted-foreground">
          From those lines you can <span className="font-medium">reattribute a sale</span> to a
          different rep. That changes who it counts for everywhere, immediately, including the
          rep&rsquo;s own view of their numbers. Reattribution is for correcting a single sale that
          landed on the wrong person. If an entire sales account is credited to the wrong rep, fix
          the alias on <span className="font-medium">Products &amp; reps</span> instead, so future
          uploads land correctly rather than needing the same correction every month.
        </p>
      </section>
      <Separator />

      <section id="st-transactions" aria-labelledby="st-transactions-heading">
        <div className="flex items-center gap-2 mb-2">
          <Receipt className="h-4 w-4" aria-hidden="true" />
          <h3 id="st-transactions-heading" className="font-semibold">
            Transactions
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Every figure on Sales Trackers is a sum of individual invoice lines, and{' '}
          <span className="font-medium">Transactions</span> is where you read those lines for the
          period you have selected. It is the page to open when a total looks wrong, because it
          shows you what actually went into it.
        </p>
        <p className="text-sm text-muted-foreground mb-2">You can narrow the list by:</p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Search</span>, which matches the customer and the sales
            account.
          </li>
          <li>
            <span className="font-medium">Rep</span>, including{' '}
            <span className="font-medium">Unattributed</span> for lines not yet linked to anybody
            and <span className="font-medium">House accounts</span> for sales that belong to the
            company rather than to a person.
          </li>
          <li>
            <span className="font-medium">Category</span>, to look at software, service or
            remediation on their own.
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          The list is paged, and the export gives you everything that matches your filters rather
          than only the page on screen.
        </p>
        <ProTip>
          <span className="font-medium">Unattributed is the first place to look</span> when a
          rep&rsquo;s total seems low or the company total does not match what the reps add up to.
          Those lines are real sales that have not been linked to a person yet, so they count for
          the company but for nobody individually.
        </ProTip>
      </section>
      <Separator />

      <section id="st-targets" aria-labelledby="st-targets-heading">
        <div className="flex items-center gap-2 mb-2">
          <Target className="h-4 w-4" aria-hidden="true" />
          <h3 id="st-targets-heading" className="font-semibold">
            Targets (Administrators)
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          The <span className="font-medium">Targets</span> page sets the year for the company and
          for every rep in one place. Each row carries three figures:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Overall</span>: the whole target.
          </li>
          <li>
            <span className="font-medium">Software</span> and{' '}
            <span className="font-medium">Service</span>: the split within it.
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          Type an annual figure and it is divided across the twelve months for you. If you fill in
          Software and Service and leave Overall empty, Overall adds them up and keeps following
          them as you edit. Type your own Overall and it stays exactly as you typed it, even if that
          is deliberately more or less than the two parts.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          The bottom row totals the rep targets and compares them with the company figure, which is
          the check that used to mean opening every rep in turn. Where a target was set month by
          month, the row says so and warns you before an even split replaces it.{' '}
          <span className="font-medium">Month level detail</span> on a row opens the individual
          months.
        </p>
        <ProTip>
          <span className="font-medium">An empty box means no target, not a target of zero.</span> A
          year with only a few months filled in is measured on those months, rather than being
          reported as a shortfall against the empty ones.
        </ProTip>
      </section>
      <Separator />

      <section id="st-forecast" aria-labelledby="st-forecast-heading">
        <div className="flex items-center gap-2 mb-2">
          <CalendarClock className="h-4 w-4" aria-hidden="true" />
          <h3 id="st-forecast-heading" className="font-semibold">
            Weekly forecast (Administrators)
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Built for the Monday sales meeting. For each rep it shows last week&rsquo;s forecast
          against this week&rsquo;s, and what has actually been invoiced, against the month&rsquo;s
          target. You can reopen an earlier week to see the figures as they stood on that day.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          Two things about it are worth understanding, because both surprise people:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">The forecast is the CRM&rsquo;s weighted pipeline</span>,
            so a deal that is not in the CRM does not appear here, however certain it is. Keeping
            the pipeline current is what makes this page worth reading.
          </li>
          <li>
            <span className="font-medium">Forecast and invoiced are never added together.</span>{' '}
            They sit side by side on purpose. A deal that closes stops being a forecast and becomes
            an invoice, so adding the two would count the same money twice.
          </li>
        </ul>
        <ProTip>
          Figures are captured automatically every Monday morning and{' '}
          <span className="font-medium">cannot be worked out after the fact</span>. The comparison
          starts from the first capture and fills in from there, so a week that was never captured
          stays blank permanently.
        </ProTip>
      </section>
      <Separator />

      <section id="st-upload" aria-labelledby="st-upload-heading">
        <div className="flex items-center gap-2 mb-2">
          <Upload className="h-4 w-4" aria-hidden="true" />
          <h3 id="st-upload-heading" className="font-semibold">
            Getting the data in (Administrators)
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Nothing arrives automatically. Sales Trackers is only as current as the last upload, which
          is a monthly job done by hand. The upload page is reached from the{' '}
          <span className="font-medium">Upload</span> button on Overview rather than from the menu,
          because it is a once a month task.
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>Export the billings report from QuickBooks.</li>
          <li>
            Choose the file under <span className="font-medium">Monthly upload</span>.
          </li>
          <li>
            Set the <span className="font-medium">Rate to CAD</span> if the file is for the
            Australian entity. A converted Australian month reads roughly 10% below the raw
            QuickBooks column, which is correct rather than a fault.
          </li>
          <li>
            Read <span className="font-medium">What will be imported</span>. Nothing is saved at
            this step, so this is your chance to catch a wrong file or a wrong month.
          </li>
          <li>Commit the import.</li>
        </ol>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Previous uploads</span> at the bottom of the page is the
          history, and it is the quickest way to answer when a month was last brought in. The same
          page also imports the <span className="font-medium">legacy Sales Tracker workbook</span>,
          which is how the years that predate QuickBooks reporting were loaded.
        </p>
        <ProTip>
          Changing the exchange rate applies to <span className="font-medium">new rows only</span>.
          Sales already imported keep the rate they were brought in at, so the historical figures do
          not move underneath a report somebody has already circulated.
        </ProTip>
      </section>
      <Separator />

      <section id="st-attribution" aria-labelledby="st-attribution-heading">
        <div className="flex items-center gap-2 mb-2">
          <UserCog className="h-4 w-4" aria-hidden="true" />
          <h3 id="st-attribution-heading" className="font-semibold">
            Products &amp; reps (Administrators)
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          QuickBooks sends sales account names as free text, and this page is where those names are
          given meaning. It lists every sales account that has ever arrived, what each one counts
          as, and what it has billed. New accounts appear here automatically after an upload, which
          makes this the page to visit once the monthly import is done.
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Categorise</span> an account as software, service or
            remediation so it lands in the right part of every breakdown.
          </li>
          <li>
            <span className="font-medium">Alias</span> an account to a rep so their sales count for
            them from now on.
          </li>
          <li>
            <span className="font-medium">Manage the rep list</span>, including adding somebody
            before their first sale so that a target can be set for them.
          </li>
        </ul>
        <ProTip>
          <span className="font-medium">
            Making a product or rep inactive retires it, it does not remove it.
          </span>{' '}
          A retired product or rep drops out of the drop-downs and stops the CRM asking you to
          categorise it, but every sale it ever made keeps counting in the totals and in both
          breakdowns. That is deliberate: if retiring something erased its history, the product
          split would stop adding up to the company total.
        </ProTip>
      </section>
    </>
  )
}
